/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module MessageItemsModule */

import { IContentRewriteProfile } from 'generated-types';
import { ContentRewriteProfile } from 'object-types';

import {
    MessageItem,
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { RspContentRewriteRuleConfigItem } from './rsp-content-rewrite-rule.config-item.factory';
import { ReqContentRewriteRuleConfigItem } from './req-content-rewrite-rule.config-item.factory';

/**
 * Partial type of Content Rewrite Profile.
 */
type TContentRewriteProfileParital = Omit<IContentRewriteProfile,
'rsp_rewrite_rules' | 'req_rewrite_rules'>;

/**
 * Content Rewrite Rule Config type.
 */
interface IContentRewriteRuleConfig extends TContentRewriteProfileParital {
    req_rewrite_rules: RepeatedMessageItem<ReqContentRewriteRuleConfigItem>;
    rsp_rewrite_rules: RepeatedMessageItem<RspContentRewriteRuleConfigItem>;
}

/**
 * @description
 *
 *   Content Rewrite Profile Message Item.
 *
 * @author Sarthak kapoor
 */
export class ContentRewriteProfileConfigItem extends
    withEditChildMessageItemMixin(MessageItem)<IContentRewriteRuleConfig> {
    constructor(args = {}) {
        const extendedArgs = {
            objectType: ContentRewriteProfile,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Remove the selected RSP Content Rule.
     */
    public removeRspContentRule(rule: RspContentRewriteRuleConfigItem): void {
        this.config.rsp_rewrite_rules.removeByMessageItem(rule);
    }

    /**
     * Remove the selected REQ Content Rule.
     */
    public removeReqContentRule(rule: ReqContentRewriteRuleConfigItem): void {
        this.config.req_rewrite_rules.removeByMessageItem(rule);
    }

    /**
     * Add RSP Content Rule.
     */
    public addRspContentRule(): void {
        this.addChildMessageItem({
            field: 'rsp_rewrite_rules',
        });
    }

    /**
     * Add REQ Content Rule.
     */
    public addReqContentRule(): void {
        this.addChildMessageItem({
            field: 'req_rewrite_rules',
        });
    }

    /**
     * Edit the selected RSP content Rule.
     */
    public editRspContentRule(rule: RspContentRewriteRuleConfigItem): void {
        this.editChildMessageItem({
            field: 'rsp_rewrite_rules',
            messageItem: rule,
            modalBindings: {
                isEditing: true,
            },
        });
    }

    /**
     * Edit the selected REQ content Rule.
     */
    public editReqContentRule(rule: ReqContentRewriteRuleConfigItem): void {
        this.editChildMessageItem({
            field: 'req_rewrite_rules',
            messageItem: rule,
            modalBindings: {
                isEditing: true,
            },
        });
    }
    /**
     * Remove all request rewrite and response rewrite rules.
     */
    public clearContentRewriteRules(): void {
        this.config.req_rewrite_rules.removeAll();
        this.config.rsp_rewrite_rules.removeAll();
    }
}
